import { getScenarioCosts, getNetworkData } from '@/features/scenarios/api/scenarios.api'
import { optimizationAPI } from '@/core/api/client'
import { queryKeys, designResultKey } from './queryKeys'

/**
 * The four queries the stat cards live on, as framework-neutral options
 * (Phase 1I).
 *
 * Vue Query and React Query both take `{ queryKey, queryFn, ...options }`, so
 * the key AND the options live here once and each framework's hook spreads
 * them. That matters more than it looks: these queries are shared with the
 * Customize Map modal and the map itself, and a React hook that retyped a key —
 * or quietly took the client's default `retry` instead of this one's 3 — would
 * split the cache in two and nothing would say so.
 *
 * Nothing here imports vue or react. The Vue composables in `useQueries.js` and
 * `useDesignResult.js` consume these today; the React cards consume them next.
 */

export type Workspace = { workspaceId: number }

/** What the network endpoint is asked for, derived from the scenario's NAME. */
export function networkModeFor(scenarioName: string | null | undefined): 'baseline' | 'optimized' {
  return (scenarioName || 'Current Baseline') === 'Current Baseline' ? 'baseline' : 'optimized'
}

/**
 * The scenario a comparison is measured against. 0, NaN, null and negatives all
 * mean "not chosen", and scenario 1 is the fallback — the id the query used to
 * be hard-coded to.
 */
export function resolveBaselineScenarioId(raw: unknown): number {
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : 1
}

/** A scenario's costs and optimisation results — the source of every cost card. */
export function scenarioCostsOptions({
  scenarioId,
  resultType = 'optimization',
  workspaceId,
}: { scenarioId: number | null; resultType?: string } & Workspace) {
  return {
    queryKey: queryKeys.scenarioCosts(scenarioId, resultType, workspaceId),
    queryFn: () => getScenarioCosts(scenarioId, resultType, workspaceId),
    // A cost request that fails is worth retrying: the cards render an em dash
    // until it succeeds, and three attempts is the difference between a blip
    // and a visible "Costs unavailable".
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(500 * (attemptIndex + 1), 2000),
    // 30s stops a re-fetch loop on scenario switch; invalidation after an
    // optimisation or a simulation is what forces freshness.
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    enabled: scenarioId != null && scenarioId > 0,
  }
}

/**
 * The baseline's costs. Always `optimization`: a baseline is what the network
 * costs when optimised, and comparing against a simulation run would answer a
 * different question.
 */
export function baselineCostsOptions({
  baselineScenarioId,
  workspaceId,
}: { baselineScenarioId: unknown } & Workspace) {
  const id = resolveBaselineScenarioId(baselineScenarioId)
  return { ...scenarioCostsOptions({ scenarioId: id, resultType: 'optimization', workspaceId }), enabled: workspaceId >= 0 }
}

/** The facilities, lanes and customers of a scenario. */
export function networkDataOptions({
  scenarioId,
  mode = 'baseline',
  workspaceId,
}: { scenarioId: number | null; mode?: string } & Workspace) {
  return {
    queryKey: queryKeys.networkData(scenarioId, mode, workspaceId),
    queryFn: () => getNetworkData(scenarioId, mode, workspaceId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Keep the previous network on screen while the next one loads, rather than
    // blanking the map and the cards between scenarios. Typed loosely on purpose:
    // the payload's shape comes from a plain-JS service layer, and both query
    // clients infer it differently.
    placeholderData: <T,>(previous: T): T => previous,
    enabled: scenarioId != null && scenarioId > 0,
  }
}

/**
 * The last design-under-uncertainty result, or null.
 *
 * null is not "loading": it means no design run has happened, which the Cost of
 * Averaging card renders as an em dash and must never render as $0. The writer
 * (`useDesignResultWriter`) pushes into this key with `setQueryData`, so any
 * reader that keys differently goes permanently stale.
 */
export function designResultOptions({
  scenarioId,
  workspaceId,
}: { scenarioId: number | null } & Workspace) {
  const id = Number(scenarioId ?? 0)
  return {
    queryKey: designResultKey(id, workspaceId),
    queryFn: async () => {
      const cfg = await optimizationAPI.getOptimizationConfig(id, workspaceId)
      return cfg?.config?.uncertaintyResult || null
    },
    staleTime: 30 * 1000,
    enabled: id > 0,
  }
}
